import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Container } from "reactstrap";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { fetchNotificationSettings } from "../../store/remediation/remediationSlice";
import RemediationSnackbarProvider from "./components/common/SnackbarHost";
import { useIsRemediationAdmin, useFetchErrorCodesOnce } from "./components/common/hooks";
import RequestsList from "./components/RequestsList";
import IamStackPanel from "./components/IamStack";
import NotificationSettingsPanel from "./components/NotificationSettings";
import {
  hubPageTitleSx,
  hubTabsSx,
  hubTabTrackSx,
  HUB_MUTED,
  remediationHubSx,
} from "./components/common/filterBarStyles";
import { RemediationHubThemeProvider } from "./components/common/hubTheme";

export const TAB_REQUESTS = "requests";
export const TAB_IAM = "iam";
export const TAB_NOTIF = "notifications";

/**
 * Remediation Hub — SecOps menu page.
 *
 * Internal sub-tabs:
 *   - Requests (default landing) — urgency-sorted list, polling, bulk actions
 *   - IAM Stack — CFN template viewer with copy/download
 *   - Notification Settings — admin only
 *
 * Above the sub-tabs we surface a banner when the tenant is paused
 * (read-only — operators flip it via SQL per the integration guide).
 */
const RemediationHub = () => {
  const dispatch = useDispatch();
  const isAdmin = useIsRemediationAdmin();
  useFetchErrorCodesOnce();
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === "undefined") return TAB_REQUESTS;
    return window.location.hash?.replace("#", "") || TAB_REQUESTS;
  });

  // Tenant settings drive both the "paused" banner above the sub-tabs and the
  // schedule validation in the Request Fix form / Re-schedule modal. Fetch once.
  const notifSettings = useSelector((s) => s.remediation?.notificationSettings);
  useEffect(() => {
    dispatch(fetchNotificationSettings());
  }, [dispatch]);

  useEffect(() => {
    document.title = "Remediation Hub";
  }, []);

  const handleTabChange = (_e, val) => {
    setActiveTab(val);
    try {
      window.history.replaceState(null, "", `#${val}`);
    } catch (_e) {
      /* no-op */
    }
  };

  const goToIamTab = () => {
    setActiveTab(TAB_IAM);
    try {
      window.history.replaceState(null, "", `#${TAB_IAM}`);
    } catch (_e) {
      /* no-op */
    }
  };

  const isPaused = Boolean(notifSettings?.data?.is_paused);

  // Tabs available to the user — Notification Settings is admin-only.
  const tabs = useMemo(() => {
    const items = [
      { value: TAB_REQUESTS, label: "Tasks" },
      { value: TAB_IAM, label: "IAM Stack" },
    ];
    if (isAdmin) items.push({ value: TAB_NOTIF, label: "Notification Settings" });
    return items;
  }, [isAdmin]);

  // If a non-admin lands on the Notifications tab via hash, fall back to Requests.
  useEffect(() => {
    if (activeTab === TAB_NOTIF && !isAdmin) {
      setActiveTab(TAB_REQUESTS);
    }
  }, [activeTab, isAdmin]);

  return (
    <RemediationHubThemeProvider>
    <RemediationSnackbarProvider>
      <div className="page-content" style={{ backgroundColor: "#fff" }}>
        <Container fluid>
      <Box sx={{ pt: 1.5, pb: 4, width: "100%", maxWidth: "100%", ...remediationHubSx }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={hubPageTitleSx}>
            Remediation Hub
          </Typography>
          <Typography variant="body2" sx={{ fontSize: 13, color: HUB_MUTED, maxWidth: 720 }}>
            File one-click fixes for security findings, schedule and approve them, and watch them
            execute against your AWS accounts.
          </Typography>
        </Box>

        {isPaused && (
          <Alert
            severity="warning"
            icon={<WarningAmberIcon />}
            sx={{ mb: 2, borderRadius: "10px" }}
          >
            Remediation is paused for this tenant. New tasks can still be drafted, but they will
            not be picked up for execution. Contact support if you need this lifted.
          </Alert>
        )}

        {notifSettings?.status === "loading" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, color: HUB_MUTED, fontSize: 13 }}>
            <CircularProgress size={14} /> Loading tenant settings…
          </Box>
        )}

        <Box sx={{ ...hubTabTrackSx, mb: 2.5 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={hubTabsSx}
          >
            {tabs.map((t) => (
              <Tab key={t.value} value={t.value} label={t.label} />
            ))}
          </Tabs>
        </Box>

        {activeTab === TAB_REQUESTS && <RequestsList onGoToIamTab={goToIamTab} />}
        {activeTab === TAB_IAM && <IamStackPanel />}
        {activeTab === TAB_NOTIF && isAdmin && <NotificationSettingsPanel />}
      </Box>
        </Container>
      </div>
    </RemediationSnackbarProvider>
    </RemediationHubThemeProvider>
  );
};

export default RemediationHub;
